import { lazy, useCallback, useEffect } from 'react';
import { PiPlugBold } from 'react-icons/pi';

import JourneyLogo from '@lib/core/src/assets/logo/journey-logo-black.svg?react';
import { Avatar, AvatarGroup } from '@lib/core/src/components/Avatar';
import palette from '@lib/core/src/theme/palette';
import { IconButton } from '@mui/material';
import { RiMenu2Line, RiUserAddLine } from '@remixicon/react';

import {
  NylasGrantDtoProvider,
  UserDetailsType,
  UnifiedConnectionDtoProvider,
  ChecklistItemDtoStatus
} from '@/api/generated';
import GoogleIcon from '@/assets/third-party-logos/google/google-calendar.svg?react';
import GoogleDriveLogo from '@/assets/third-party-logos/google/google-drive.svg?react';
import HubspotLogo from '@/assets/third-party-logos/hubspot/hubspot.svg?react';
import MicrosoftIcon from '@/assets/third-party-logos/microsoft/microsoft-calendar.svg?react';
import SlackLogo from '@/assets/third-party-logos/slack/slack.svg?react';
import useLoginContext from '@/context/LoginFlowContextProvider/useLoginContext';
import RoutesMap from '@/enums/RoutesMap';
import UserRole from '@/enums/UserRoles';
import useConnections from '@/hooks/Integrations/useConnections';
import useIntegrations from '@/hooks/Integrations/useIntegrations';
import { useCheckList } from '@/hooks/useCheckList';
import { useCustomNavigate } from '@/hooks/useCustomNavigate';
import useModal from '@/hooks/useModal/useModal';
import { CheckListEnumTitles } from '@/pages/HomePage/components/checklist/CheckListConfig';
import { useSidebarActions } from '@/store/sidebarStore';
import { useIsSmallScreen } from '@/utils/mediaUtil';

import {
  ButtonsRowReverse,
  CompanyLogo,
  HeaderContainer,
  CompanyImg,
  MenuIconContainer,
  JourneyLogoContainer,
  AddUserIcon,
  IntegrationsContainer
} from './HeaderStyles';

const LazyUserModal = lazy(() => import('@/components/modals/UserModal/UserModal'));

const Header = () => {
  const { currentUser, currentAccount } = useLoginContext();
  const navigate = useCustomNavigate();
  const { showModal } = useModal();
  const { userGrant } = useIntegrations();
  const { conntectionList } = useConnections();
  const { toggleSidebar } = useSidebarActions();
  const { isItemCompleted, updateCheckList } = useCheckList();
  const isGoogleMicrosoftIntegrated = isItemCompleted(CheckListEnumTitles.IntegrateGoogleMicrosoft);
  const isSmallScreen = useIsSmallScreen();
  const getProviderLogo = (provider: UnifiedConnectionDtoProvider) => {
    if (provider === UnifiedConnectionDtoProvider.hubspot) {
      return <HubspotLogo width="16px" height="16px" />;
    }
    if (provider === UnifiedConnectionDtoProvider.slackbot) {
      return <SlackLogo width="16px" height="16px" />;
    }
    if (provider === UnifiedConnectionDtoProvider.googleDrive) {
      return <GoogleDriveLogo width="16px" height="16px" />;
    }
  };

  const hasGoogle = userGrant ? userGrant.provider === NylasGrantDtoProvider.google : false;
  const hasMicrosoft = userGrant ? userGrant.provider === NylasGrantDtoProvider.microsoft : false;

  const onAddUserClick = useCallback(() => {
    const modal = showModal(LazyUserModal, {
      onClose: () => modal.hide(),
      onConfirm: () => {
        modal.hide();
      }
    });
  }, [showModal]);

  const isAdmin =
    currentUser?.type === UserDetailsType.ceo || currentUser?.roleId === UserRole.ADMIN;

  useEffect(() => {
    if (isGoogleMicrosoftIntegrated || (!hasGoogle && !hasMicrosoft)) return;
    updateCheckList({
      title: CheckListEnumTitles.IntegrateGoogleMicrosoft,
      status: ChecklistItemDtoStatus.completed
    });
  }, [isGoogleMicrosoftIntegrated, updateCheckList, hasGoogle, hasMicrosoft]);

  return (
    <HeaderContainer>
      <MenuIconContainer>
        <IconButton onClick={toggleSidebar}>
          <RiMenu2Line size={16} color={palette.black} />
        </IconButton>
      </MenuIconContainer>

      <JourneyLogoContainer>
        <JourneyLogo onClick={() => navigate(RoutesMap.Home)} />
      </JourneyLogoContainer>
      <ButtonsRowReverse>
        <CompanyLogo
          size="small"
          onClick={() => navigate(RoutesMap.CompanyProfile)}
          disabled={isSmallScreen}
        >
          {currentAccount?.logoUrl ? (
            <CompanyImg src={currentAccount.logoUrl} alt="🏢" />
          ) : (
            <span role="img" aria-label="Company">
              🏢
            </span>
          )}
        </CompanyLogo>
        {isAdmin && (
          <AddUserIcon onClick={onAddUserClick}>
            <RiUserAddLine size={12} style={{ marginRight: '4px' }} />
            Invite
          </AddUserIcon>
        )}
        <IntegrationsContainer
          onClick={() => {
            navigate(RoutesMap.Integrations);
          }}
        >
          <AvatarGroup sx={{ borderColor: palette.grey[1200], borderWidth: '1px' }}>
            <Avatar>
              <PiPlugBold size={16} color={palette.grey[400]} />
            </Avatar>
            {hasGoogle && (
              <Avatar>
                <GoogleIcon />
              </Avatar>
            )}
            {hasMicrosoft && (
              <Avatar>
                <MicrosoftIcon />
              </Avatar>
            )}
            {conntectionList?.map(connection => (
              <Avatar key={connection.connectionId}>
                {getProviderLogo(connection.provider as UnifiedConnectionDtoProvider)}
              </Avatar>
            ))}
          </AvatarGroup>
        </IntegrationsContainer>
      </ButtonsRowReverse>
    </HeaderContainer>
  );
};

export default Header;
